import { jsPDF } from 'jspdf';
import * as autoTableNs from 'jspdf-autotable';
import { BRAND } from '@/config/brand';
import { Venta } from './supabase';
import { VentaDetalleItem } from './ventas';

const MARGIN = 14;
const AZUL: [number, number, number] = [37, 99, 235];
const OSCURO: [number, number, number] = [31, 41, 55];
const GRIS: [number, number, number] = [120, 130, 145];
const LINEA: [number, number, number] = [226, 232, 240];

/* ----------------------------- utilidades ----------------------------- */

export const numeroVenta = (id: string): string => {
  const raw = (id || '').replace(/[^a-zA-Z0-9]/g, '');
  return `V-${raw.slice(0, 8).toUpperCase()}`;
};

const idArchivo = (id: string): string => (id || '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase();

const nro = (valor: number): string => {
  const n = Number.isFinite(valor) ? valor : 0;
  return n.toLocaleString('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fechaHoraDoc = (d: Date): string =>
  d.toLocaleString('es-BO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const capitalizar = (texto: string): string =>
  texto ? texto.charAt(0).toUpperCase() + texto.slice(1) : '—';

/* --------------------- resolución robusta de AutoTable --------------------- */

type AutoTableFn = (doc: jsPDF, opciones: Record<string, unknown>) => void;

/**
 * jspdf-autotable puede resolverse como export default o como función nombrada
 * según el entorno de empaquetado. Resolvemos siempre una función válida y, si
 * no existe, usamos la que el plugin registra en la instancia de jsPDF.
 */
const resolverAutoTable = (): AutoTableFn => {
  const ns = autoTableNs as unknown as Record<string, unknown>;
  const candidatos = [ns.default, ns.autoTable, ns];
  for (const candidato of candidatos) {
    if (typeof candidato === 'function') {
      return candidato as AutoTableFn;
    }
  }
  return (doc, opciones) => {
    const instancia = doc as unknown as {
      autoTable?: (o: Record<string, unknown>) => void;
    };
    if (typeof instancia.autoTable !== 'function') {
      throw new Error('jsPDF-AutoTable no está disponible.');
    }
    instancia.autoTable(opciones);
  };
};

const autoTable = resolverAutoTable();

const finalYTabla = (doc: jsPDF): number => {
  const meta = (doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable;
  return meta && typeof meta.finalY === 'number' ? meta.finalY : 0;
};

/* ------------------------- logo -> dataURL (cache) ------------------------- */

let logoCache: string | null = null;
let logoPromesa: Promise<string | null> | null = null;

const conTiempoLimite = <T,>(p: Promise<T>, ms: number, porDefecto: T): Promise<T> =>
  Promise.race([
    p,
    new Promise<T>((resolve) => {
      setTimeout(() => resolve(porDefecto), ms);
    }),
  ]);

export const cargarLogoDataUrl = (): Promise<string | null> => {
  if (logoCache) return Promise.resolve(logoCache);
  if (logoPromesa) return logoPromesa;

  logoPromesa = conTiempoLimite(
    (async () => {
      try {
        const res = await fetch(BRAND.logoUrl);
        const blob = await res.blob();
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        logoCache = dataUrl;
        return dataUrl;
      } catch {
        return null;
      }
    })(),
    2500,
    null
  );

  return logoPromesa;
};

const dimensionLogo = (dataUrl: string): Promise<{ w: number; h: number }> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });
    img.onerror = () => resolve({ w: 1, h: 1 });
    img.src = dataUrl;
  });

/* ------------------------------ cabecera ------------------------------ */

interface OpcionesCabecera {
  titulo: string;
  derecha: string[];
}

const dibujarCabecera = async (
  doc: jsPDF,
  logo: string | null,
  { titulo, derecha }: OpcionesCabecera
): Promise<number> => {
  const pageW = doc.internal.pageSize.getWidth();
  let xText = MARGIN;

  if (logo) {
    try {
      const { w, h } = await conTiempoLimite(dimensionLogo(logo), 2000, { w: 1, h: 1 });
      const alto = 18;
      const ancho = Math.min(46, alto * (w / h));
      if (ancho > 1 && alto > 1) {
        const formato = logo.includes('image/png') ? 'PNG' : 'JPEG';
        doc.addImage(logo, formato, MARGIN, 11, ancho, alto);
        xText = MARGIN + ancho + 5;
      }
    } catch {
      xText = MARGIN;
    }
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...OSCURO);
  doc.text(BRAND.name, xText, 19);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...GRIS);
  doc.text(BRAND.tagline, xText, 25);

  // lado derecho
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...AZUL);
  doc.text(titulo, pageW - MARGIN, 17, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...GRIS);
  derecha.forEach((linea, idx) => {
    doc.text(linea, pageW - MARGIN, 23 + idx * 4, { align: 'right' });
  });

  doc.setDrawColor(...LINEA);
  doc.setLineWidth(0.4);
  doc.line(MARGIN, 34, pageW - MARGIN, 34);

  return 40;
};

/* --------------------------- bloque de información --------------------------- */

const dibujarInfoOperacion = (doc: jsPDF, pares: [string, string][], startY: number): number => {
  const pageW = doc.internal.pageSize.getWidth();
  const boxW = pageW - MARGIN * 2;
  const colW = boxW / 2;
  const rowH = 7;
  const filas = Math.ceil(pares.length / 2);
  const boxH = filas * rowH + 4;

  doc.setFillColor(246, 248, 250);
  doc.setDrawColor(...LINEA);
  doc.roundedRect(MARGIN, startY, boxW, boxH, 1.5, 1.5, 'FD');

  pares.forEach((par, idx) => {
    const col = idx % 2;
    const fila = Math.floor(idx / 2);
    const x = MARGIN + 4 + col * colW;
    const yy = startY + 6.5 + fila * rowH;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...GRIS);
    doc.text(`${par[0]}:`, x, yy);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...OSCURO);
    doc.text(String(par[1]), x + 40, yy);
  });

  return startY + boxH + 3;
};

/* ------------------------------ total destacado ------------------------------ */

const dibujarTotal = (doc: jsPDF, total: number, startY: number): void => {
  const pageW = doc.internal.pageSize.getWidth();
  const w = 84;
  const x = pageW - MARGIN - w;

  doc.setFillColor(...AZUL);
  doc.roundedRect(x, startY, w, 12, 1.5, 1.5, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.text('TOTAL DE LA VENTA', x + 4, startY + 7.6);

  doc.setFontSize(12);
  doc.text(`Bs. ${nro(total)}`, x + w - 4, startY + 7.8, { align: 'right' });
};

/* ------------------------------ pie de página ------------------------------ */

const dibujarPiePaginas = (doc: jsPDF): void => {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const total = doc.getNumberOfPages();

  for (let i = 1; i <= total; i += 1) {
    doc.setPage(i);
    doc.setDrawColor(...LINEA);
    doc.setLineWidth(0.4);
    doc.line(MARGIN, pageH - 12, pageW - MARGIN, pageH - 12);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...GRIS);
    doc.text(BRAND.name, MARGIN, pageH - 7);
    doc.text(`Página ${i} de ${total}`, pageW - MARGIN, pageH - 7, { align: 'right' });
  }
};

/* ------------------------------ descarga ------------------------------ */

const descargar = (doc: jsPDF, nombre: string): void => {
  try {
    doc.save(nombre);
    return;
  } catch {
    // Si el entorno bloquea la descarga directa, generamos el archivo manualmente.
  }

  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombre;
  enlace.rel = 'noopener';
  enlace.style.display = 'none';
  document.body.appendChild(enlace);
  enlace.click();
  document.body.removeChild(enlace);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

/* --------------------------- PDF de una sola venta --------------------------- */

export const generarPdfVenta = async (
  venta: Venta,
  items: VentaDetalleItem[],
  empleado: string
): Promise<void> => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const logo = await cargarLogoDataUrl();

  let y = await dibujarCabecera(doc, logo, {
    titulo: 'DETALLE DE VENTA',
    derecha: [`N.º ${numeroVenta(venta.id)}`, `Emitido: ${fechaHoraDoc(new Date())}`],
  });

  y = dibujarInfoOperacion(
    doc,
    [
      ['Cliente', venta.nombre_factura || 'Consumidor Final'],
      ['NIT / CI', venta.nit || '—'],
      ['Empleado responsable', empleado || '—'],
      ['Método de pago', capitalizar(venta.metodo_pago)],
      ['Monto recibido', `Bs. ${nro(Number(venta.monto_recibido))}`],
      ['Cambio', `Bs. ${nro(Number(venta.cambio))}`],
    ],
    y
  );

  autoTable(doc, {
    startY: y + 3,
    head: [['Producto', 'Cantidad', 'Precio unitario (Bs.)', 'Subtotal (Bs.)']],
    body: items.map((it) => [
      it.codigo ? `${it.nombre}\n${it.codigo}` : it.nombre,
      String(it.cantidad),
      nro(it.precioUnitario),
      nro(it.subtotal),
    ]),
    theme: 'grid',
    styles: { font: 'helvetica', fontSize: 9, cellPadding: 2.6, textColor: OSCURO, lineColor: LINEA },
    headStyles: { fillColor: AZUL, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
    columnStyles: {
      0: { cellWidth: 'auto', halign: 'left' },
      1: { cellWidth: 24, halign: 'center' },
      2: { cellWidth: 42, halign: 'right' },
      3: { cellWidth: 42, halign: 'right' },
    },
    margin: { left: MARGIN, right: MARGIN },
  });

  const finTabla = finalYTabla(doc) || y + 10;
  dibujarTotal(doc, Number(venta.total), finTabla + 6);

  dibujarPiePaginas(doc);
  descargar(doc, `Venta-${idArchivo(venta.id)}.pdf`);
};

/* ------------------------- PDF del historial completo ------------------------- */

export const generarPdfHistorial = async (
  ventas: Venta[],
  empleados: Record<string, string>,
  periodoLabel: string
): Promise<void> => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const logo = await cargarLogoDataUrl();

  let y = await dibujarCabecera(doc, logo, {
    titulo: 'HISTORIAL DE VENTAS',
    derecha: [`Período: ${periodoLabel}`, `Generado: ${fechaHoraDoc(new Date())}`],
  });

  const cantidad = ventas.length;
  const ingresos = ventas.reduce((s, v) => s + Number(v.total || 0), 0);
  const recibido = ventas.reduce((s, v) => s + Number(v.monto_recibido || 0), 0);
  const cambios = ventas.reduce((s, v) => s + Number(v.cambio || 0), 0);

  y = dibujarInfoOperacion(
    doc,
    [
      ['Cantidad de ventas', String(cantidad)],
      ['Ingresos por ventas', `Bs. ${nro(ingresos)}`],
      ['Total recibido', `Bs. ${nro(recibido)}`],
      ['Total de cambios', `Bs. ${nro(cambios)}`],
    ],
    y
  );

  autoTable(doc, {
    startY: y + 3,
    head: [['N.º de venta', 'Fecha', 'Cliente', 'Empleado', 'Método de pago', 'Total (Bs.)']],
    body: ventas.map((v) => [
      numeroVenta(v.id),
      fechaHoraDoc(new Date(v.fecha)),
      v.nombre_factura || '—',
      empleados[v.usuario_id] || '—',
      capitalizar(v.metodo_pago),
      nro(Number(v.total)),
    ]),
    foot: [['', '', '', '', 'Total general', nro(ingresos)]],
    theme: 'grid',
    styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 2.4, textColor: OSCURO, lineColor: LINEA },
    headStyles: { fillColor: AZUL, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
    footStyles: { fillColor: OSCURO, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'right' },
    columnStyles: {
      0: { cellWidth: 26 },
      1: { cellWidth: 30 },
      2: { cellWidth: 'auto' },
      3: { cellWidth: 30 },
      4: { cellWidth: 26, halign: 'center' },
      5: { cellWidth: 26, halign: 'right' },
    },
    margin: { left: MARGIN, right: MARGIN },
  });

  dibujarPiePaginas(doc);

  const ahora = new Date();
  const sello = `${ahora.getFullYear()}${String(ahora.getMonth() + 1).padStart(2, '0')}${String(
    ahora.getDate()
  ).padStart(2, '0')}`;
  descargar(doc, `Historial-Ventas-${sello}.pdf`);
};